// src/components/Properties/ConnectorProperties/ConnectorProperties.tsx
import React from 'react';
import { Settings, Trash2, Palette, Activity, Type } from 'lucide-react';
import { useConnector } from 'src/hooks/useConnector';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useScene } from 'src/hooks/useScene';
import { Connector, connectorStyleOptions } from 'src/types';
import { arrowTypeOptions } from 'src/schemas/connector';
import { ColorPicker } from '../ColorPicker';
import { DEFAULT_COLOR } from 'src/config';
import { useColor } from 'src/hooks/useColor';

interface Props {
  id: string;
}

export const ConnectorProperties: React.FC<Props> = ({ id }) => {
  const uiStateActions = useUiStateStore((state) => state.actions);
  const connector = useConnector(id);
  const { updateConnector, deleteConnector } = useScene();
  const color = useColor(connector?.color);
  const colorValue =
    typeof color === 'string' ? color : color?.value || DEFAULT_COLOR.value;

  // Handle null connector
  if (!connector) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Settings className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            Connector Not Found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            The selected connector no longer exists
          </p>
        </div>
      </div>
    );
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this connector?')) {
      uiStateActions.setItemControls(null);
      deleteConnector(connector.id);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-4 ">
        {/* Description Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Type className="h-4 w-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
          </div>

          <div>
            <textarea
              value={connector.description || ''}
              onChange={(e) =>
                updateConnector(connector.id, { description: e.target.value })
              }
              rows={3}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-slate-400"
              placeholder="Describe what this connector represents..."
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Add a description to help others understand this connection
            </p>
          </div>
        </div>

        {/* Appearance Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Appearance
            </label>
          </div>

          {/* Line Color */}
          <div>
            <ColorPicker
              value={colorValue}
              onChange={(color) => updateConnector(connector.id, { color })}
              label="Line Color"
            />
          </div>

          {/* Line Width */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Line Width: {connector.width || 4}px
            </label>
            <input
              type="range"
              min="2"
              max="20"
              step="1"
              value={connector.width || 4}
              onChange={(e) =>
                updateConnector(connector.id, {
                  width: parseInt(e.target.value)
                })
              }
              className="w-full"
            />
          </div>

          {/* Line Style */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Line Style
            </label>
            <select
              value={connector.style || 'SOLID'}
              onChange={(e) =>
                updateConnector(connector.id, {
                  style: e.target.value as Connector['style']
                })
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            >
              {connectorStyleOptions.map((style) => (
                <option key={style} value={style}>
                  {style.charAt(0) + style.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Arrow Type */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Arrow Type
            </label>
            <select
              value={connector.arrowType || 'SINGLE'}
              onChange={(e) =>
                updateConnector(connector.id, {
                  arrowType: e.target.value as Connector['arrowType']
                })
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            >
              {arrowTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type === 'NONE'
                    ? 'No Arrows'
                    : type === 'SINGLE'
                      ? 'Single Arrow'
                      : 'Double Arrows'}
                </option>
              ))}
            </select>
          </div>

          {/* Arrow Direction - Only show when arrowType is SINGLE */}
          {connector.arrowType === 'SINGLE' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Arrow Direction
              </label>
              <select
                value={connector.arrowDirection || 'forward'}
                onChange={(e) =>
                  updateConnector(connector.id, {
                    arrowDirection: e.target.value as 'forward' | 'backward'
                  })
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
              >
                <option value="forward">Forward (arrow at end →)</option>
                <option value="backward">Backward (arrow at start ←)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Position of the arrow on the connector
              </p>
            </div>
          )}

          {/* Fade at Ends Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-slate-600 dark:bg-slate-800/50">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Fade at Ends
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Reduce opacity at connector endpoints
              </p>
            </div>
            <button
              onClick={() =>
                updateConnector(connector.id, { fadeEnds: !connector.fadeEnds })
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                connector.fadeEnds
                  ? 'bg-blue-600'
                  : 'bg-gray-300 dark:bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  connector.fadeEnds ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* ========== NEW: Flow Animation Section ========== */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Flow Animation
            </label>
          </div>

          {/* Enable Flow Toggle */}
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-slate-600 dark:bg-slate-800/50">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Flow Animation
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Animate data flowing through connector
              </p>
            </div>
            <button
              onClick={() =>
                updateConnector(connector.id, {
                  flowEnabled: !connector.flowEnabled
                })
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                connector.flowEnabled
                  ? 'bg-blue-600'
                  : 'bg-gray-300 dark:bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  connector.flowEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Show controls when flow is enabled */}
          {connector.flowEnabled && (
            <>
              {/* Flow Direction */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Flow Direction
                </label>
                <select
                  value={connector.flowDirection || 'forward'}
                  onChange={(e) =>
                    updateConnector(connector.id, {
                      flowDirection: e.target.value as NonNullable<
                        Connector['flowDirection']
                      >
                    })
                  }
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                >
                  <option value="forward">Forward (→)</option>
                  <option value="backward">Backward (←)</option>
                  <option value="bidirectional">Bidirectional (↔)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {connector.arrowType === 'DOUBLE'
                    ? 'Bidirectional recommended for double arrows'
                    : 'Direction of data flow'}
                </p>
              </div>

              {/* Flow Speed */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Flow Speed: {(connector.flowSpeed || 1).toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={connector.flowSpeed || 1}
                  onChange={(e) =>
                    updateConnector(connector.id, {
                      flowSpeed: parseFloat(e.target.value)
                    })
                  }
                  className="w-full"
                />
              </div>

              {/* Particle Count */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Particle Count: {connector.flowParticleCount || 5}
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={connector.flowParticleCount || 5}
                  onChange={(e) =>
                    updateConnector(connector.id, {
                      flowParticleCount: parseInt(e.target.value)
                    })
                  }
                  className="w-full"
                />
              </div>

              {/* Particle Size */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Particle Size: {connector.flowParticleSize || 8}px
                </label>
                <input
                  type="range"
                  min="4"
                  max="16"
                  step="1"
                  value={connector.flowParticleSize || 8}
                  onChange={(e) =>
                    updateConnector(connector.id, {
                      flowParticleSize: parseInt(e.target.value)
                    })
                  }
                  className="w-full"
                />
              </div>
            </>
          )}
        </div>
        {/* ================================================= */}

        {/* Delete Section */}
        <div className="pt-4 border-t border-gray-200 dark:border-slate-600">
          <button
            onClick={handleDelete}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            <Trash2 className="h-4 w-4" />
            Delete Connector
          </button>
        </div>
      </div>
    </div>
  );
};
